import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Dropdown, DropdownToggle, DropdownMenu, Row, Col } from "reactstrap";
import SimpleBar from "simplebar-react";
import { queryKeyes } from "shared/queryKeys";
import { Get } from "VesselMaster/vesselMaster.hooks";
import { useQuery } from 'react-query';
import moment from "moment";
import { truncateText } from "GenericForms/Helper";

// Utility to remove HTML tags (e.g. <strong>, <br/> etc.)
const stripHtmlTags = (html: string): string => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
};

// Get notification icon based on type or content
const getNotificationIcon = (notification: any) => {
  const message = notification.message?.toLowerCase() || "";
  const title = notification.title?.toLowerCase() || "";

  if (message.includes("vessel") || title.includes("vessel")) {
    return "bx bx-ship";
  } else if (message.includes("alert") || title.includes("alert")) {
    return "bx bx-error-circle";
  } else if (message.includes("update") || title.includes("update")) {
    return "bx bx-refresh";
  } else if (message.includes("message") || title.includes("message")) {
    return "bx bx-message-dots";
  } else if (message.includes("user") || title.includes("user")) {
    return "bx bx-user";
  }
  return "bx bx-bell";
};

// Get notification color based on priority or type
const getNotificationColor = (notification: any) => {
  const message = notification.message?.toLowerCase() || "";
  const title = notification.title?.toLowerCase() || "";

  if (message.includes("warning") || title.includes("warning")) {
    return "bg-warning";
  } else if (message.includes("success") || title.includes("success")) {
    return "bg-success";
  } else if (message.includes("info") || title.includes("info")) {
    return "bg-info";
  }
  return "bg-primary";
};

const NotificationDropdown = () => {
  const [menu, setMenu] = useState(false);

  const { data: NotificationLoggedInUserObject } = useQuery(
    [queryKeyes.notification.NotificationForLoggedInUser.key],
    async () => await Get(queryKeyes.notification.NotificationForLoggedInUser.url()),
    {
      enabled: true,
      staleTime: Infinity,
    }
  );

  return (
    <React.Fragment>
      <Dropdown
        isOpen={menu}
        toggle={() => setMenu(!menu)}
        className="dropdown d-inline-block"
        tag="li"
      >
        <DropdownToggle
          className="btn header-item noti-icon-btn noti-icon position-relative border-0 bg-transparent"
          tag="button"
          id="page-header-notifications-dropdown"
        >
          <i
            className={`mdi mdi-bell-outline icon-lg noti-icon ${NotificationLoggedInUserObject?.length > 0 ? "has-unread" : "no-unread"
              }`}
          />
          {NotificationLoggedInUserObject?.length > 0 && (
            <span className="badge bg-danger rounded-pill position-absolute unread-badge">
              {NotificationLoggedInUserObject?.length > 9 ? "9+" : NotificationLoggedInUserObject?.length}
            </span>
          )}
        </DropdownToggle>
        <DropdownMenu className="dropdown-menu-lg dropdown-menu-end p-0 shadow-lg border-0 notification-dropdown-menu">
          <div className="p-3 border-bottom bg-light notification-header">
            <Row className="align-items-center">
              <Col>
                <div className="d-flex align-items-center justify-content-between">
                  <h6 className="m-0 fw-bold notification-item" style={{ color: 'white' }}>Notifications</h6>
                  <div className="d-flex align-items-center gap-2">
                    {NotificationLoggedInUserObject?.length > 0 && (
                      <span
                        className="badge bg-light text-dark rounded-pill"
                        style={{ fontSize: '11px', opacity: 0.9 }}
                      >
                        {NotificationLoggedInUserObject?.length} new
                      </span>
                    )}
                    <i className="mdi mdi-bell" style={{ fontSize: '16px' }} />
                  </div>
                </div>
              </Col>
            </Row>
          </div>

          {/* Notification List */}
          <SimpleBar className="notification-list">
            {NotificationLoggedInUserObject?.length > 0 ? (
              NotificationLoggedInUserObject
                ?.sort((a: any, b: any) => new Date(b.created_on).getTime() - new Date(a.created_on).getTime())
                .slice(0, 9)
                .map((item: any, index: number) => (
                  <div className="notification-item" key={index}>
                    <div className="avatar-sm me-3 flex-shrink-0">
                      <span
                        className={`avatar-title ${getNotificationColor(item)} rounded-circle d-flex align-items-center justify-content-center notification-avatar`}
                      >
                        <i className={getNotificationIcon(item)} />
                      </span>
                    </div>
                    <div className="flex-grow-1 overflow-hidden">
                      <div className="d-flex justify-content-between align-items-start mb-1 p-1">
                        <h6 className="mt-0 mb-0 fw-semibold text-truncate notification-title">
                          {truncateText(item.title, 35)}
                        </h6>
                        <span className="badge bg-light text-muted rounded-pill ms-2 notification-new-badge">
                          New
                        </span>
                      </div>
                      <p className="text-muted mb-1 text-truncate notification-message">
                        {truncateText(stripHtmlTags(item.message), 65)}
                      </p>
                      <div className="d-flex align-items-center text-muted p-1">
                        <i className="mdi mdi-clock-outline me-1 notification-clock" />
                        <span className="notification-time">{moment(item.created_on).fromNow()}</span>
                      </div>
                    </div>
                  </div>
                ))
            ) : (
              <div className="text-center p-4 notification-empty">
                <i className="mdi mdi-bell-sleep text-muted mb-2 notification-empty-icon" />
                <p className="text-muted mb-0 notification-empty-text">No new notifications</p>
                <p className="text-muted mb-0 notification-empty-subtext">You're all caught up!</p>
              </div>
            )}
          </SimpleBar>
          {/* Footer */}
          <div className="p-2 border-top d-grid notification-footer">
            <Link className="btn btn-sm btn-link font-size-14 text-center text-decoration-none fw-semibold notification-footer-link" to="/notifications">
              <i className="mdi mdi-arrow-right-circle me-1" />
              View all notifications
            </Link>
          </div>
        </DropdownMenu>
      </Dropdown>
    </React.Fragment>
  );
};

export default NotificationDropdown;

